import { useMutation, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { Project, Resource } from '@/types/types';
import { API_URL } from '@/lib/apiUrl';

type UpdateResourcePayload = {
  resourceable_type: Resource['resourceable_type'];
  resourceable_id: Resource['resourceable_id'];
};

export const useChangeResourceable = (
  resourceId: Resource['id'],
  projectId?: Project['id'],
  taskId?: Resource['task_id']
) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateResourcePayload) => {
      const response = await axiosInstance.patch(
        `${API_URL}/api/v1/resources/${resourceId}/change_resourceable`,
        payload,
        {
          params: {
            project_id: projectId,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_variables, _data) => {
      // Invalidate all resource queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.list({
          projectId: projectId,
          resourceableType: 'Account',
        }).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.list({
          projectId: projectId,
          resourceableType: 'Partner',
        }).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.list({
          projectId: projectId,
          resourceableType: 'ResourcePlaceholder',
        }).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.byGrouped('task_id', {
          projectId: projectId,
        }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.uniqueByProject._def,
      });

      if (taskId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.detail(taskId).queryKey,
        });
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.all.queryKey,
      });
    },
  });
};

export default useChangeResourceable;
